import { covertTo4Digit, fixNumber } from 'utils/helper';

export const SKIP_DECIMAL_FIELD = Symbol('skip-decimal-field');

const INCOMPLETE_DECIMAL_VALUES = new Set(['.', '-', '-.']);
const FORMULA_IDENTIFIER_PATTERN = /[A-Za-z_]/;
const FORMULA_OPERATOR_PATTERN = /[+\-*/()]/;
const FORMULA_MATH_OPERATOR_PATTERN = /[+\-*/]/;
const PLAIN_NUMBER_PATTERN = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;
const TRAILING_FORMULA_OPERATOR_PATTERN = /[+\-*/]\s*$/;
const SAFE_EVALUATED_FORMULA_PATTERN = /^[\d+\-*/().\s]+$/;

export const expressionFieldFor = fieldKey => `${fieldKey}_expression`;

const normalizeGlobalAbbreviation = value =>
  String(value ?? '')
    .replace(/\s/g, '')
    .toUpperCase();

export const normalizeFormulaPayloadValue = value =>
  String(value ?? '')
    .trim()
    .replace(/\$/g, '')
    .replace(/,/g, '')
    .replace(/\b[A-Za-z_][A-Za-z0-9_]*\b/g, match => match.toUpperCase());

export const isFormulaExpressionValue = value => {
  const cleaned = normalizeFormulaPayloadValue(value);
  if (!cleaned) return false;
  if (FORMULA_IDENTIFIER_PATTERN.test(cleaned)) return true;
  return (
    FORMULA_OPERATOR_PATTERN.test(cleaned) &&
    !PLAIN_NUMBER_PATTERN.test(cleaned)
  );
};

export const hasFormulaMathOperator = value => {
  const cleaned = normalizeFormulaPayloadValue(value);
  return (
    isFormulaExpressionValue(cleaned) &&
    FORMULA_MATH_OPERATOR_PATTERN.test(cleaned)
  );
};

export const isIncompleteFormulaExpressionValue = value => {
  const cleaned = normalizeFormulaPayloadValue(value);
  if (!isFormulaExpressionValue(cleaned)) return false;
  if (TRAILING_FORMULA_OPERATOR_PATTERN.test(cleaned)) return true;

  let depth = 0;
  for (const char of cleaned) {
    if (char === '(') depth += 1;
    if (char === ')') {
      depth -= 1;
      if (depth < 0) return true;
    }
  }

  return depth !== 0;
};

export const isIncompleteDecimalValue = (value, column = {}) => {
  if (value === null || value === undefined || value === '') return false;
  if (isFormulaExpressionValue(value)) return false;
  const fixed = fixNumber(value, column.allowNegative !== false);
  return INCOMPLETE_DECIMAL_VALUES.has(fixed) || /\.$/.test(fixed);
};

export const formatDecimalDisplayValue = value => {
  if (value === null || value === undefined || value === '') return value;

  const cleaned = String(value)
    .trim()
    .replace(/\$/g, '')
    .replace(/,/g, '')
    .replace(/\s/g, '');

  if (!cleaned) return '';

  const numericValue = Number(cleaned);
  if (!Number.isFinite(numericValue)) return value;

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 4,
  }).format(numericValue);
};

const parseFormulaNumber = value => {
  if (value === null || value === undefined || value === '') return null;

  const numericValue = Number(
    String(value).trim().replace(/\$/g, '').replace(/,/g, '').replace(/\s/g, '')
  );

  return Number.isFinite(numericValue) ? numericValue : null;
};

export const evaluateFormulaDisplayValue = (value, budgetGlobals = []) => {
  const expression = normalizeFormulaPayloadValue(value);
  if (!expression) return null;

  if (!isFormulaExpressionValue(expression)) {
    return formatDecimalDisplayValue(expression);
  }

  const globalValues = new Map();
  budgetGlobals.forEach(globalItem => {
    const abbreviation = normalizeGlobalAbbreviation(globalItem?.abbreviation);
    if (!abbreviation) return;

    const numericValue = parseFormulaNumber(globalItem?.value);
    if (numericValue === null) return;
    globalValues.set(abbreviation, numericValue);
  });

  let substitutedExpression;
  try {
    substitutedExpression = expression.replace(
      /\b[A-Za-z_][A-Za-z0-9_]*\b/g,
      match => {
        const globalValue = globalValues.get(
          normalizeGlobalAbbreviation(match)
        );
        if (globalValue === undefined) {
          throw new Error(`Unknown global: ${match}`);
        }
        return `(${globalValue})`;
      }
    );
  } catch (_error) {
    return null;
  }

  if (!SAFE_EVALUATED_FORMULA_PATTERN.test(substitutedExpression)) {
    return null;
  }

  try {
    // The expression is restricted to numbers, operators, decimals, spaces,
    // and parentheses before evaluation.
    // eslint-disable-next-line no-new-func
    const evaluatedValue = Function(
      `"use strict"; return (${substitutedExpression});`
    )();
    if (!Number.isFinite(evaluatedValue)) return null;
    return formatDecimalDisplayValue(evaluatedValue);
  } catch (_error) {
    return null;
  }
};

export const normalizeDecimalPayloadValue = (value, column = {}) => {
  if (value === null || value === undefined || value === '') {
    return ['rate', 'actual', 'estimate_to_complete_override'].includes(
      column.key
    )
      ? null
      : SKIP_DECIMAL_FIELD;
  }

  const fixed = fixNumber(value, column.allowNegative !== false);
  if (INCOMPLETE_DECIMAL_VALUES.has(fixed) || /\.$/.test(fixed)) {
    return SKIP_DECIMAL_FIELD;
  }

  if (fixed === '') {
    return ['rate', 'actual', 'estimate_to_complete_override'].includes(
      column.key
    )
      ? null
      : SKIP_DECIMAL_FIELD;
  }

  return covertTo4Digit(fixed);
};
